import { BaseStore } from './BaseStore';

import stringify from 'json-stable-stringify';
import { computed, observable } from 'mobx';

import { EventManager } from 'src/utils/EventManager';
import { WindowManager } from 'src/utils/WindowManager';
import { SourceEventManager } from 'src/utils/SourceEventManager';
import { EventTypeCache, ListenForOptions } from 'src/models/configuration/ListenForOptions';
import { EventTypeDetail, EventTypes, EventPublish, EventSource, EventSubscription } from 'src/models/eventManager';
import { debug, error, LogOptions as LogOptions, info, success, TLogType, warning } from 'src/utils/log';
import { Manifest } from 'src/configuration/Manifest';

type EventBusStoreCallback<TDetail extends EventTypeDetail> = (ev: EventPublish<TDetail>) => Promise<void>;
type EventBusStoreCallbacks<TDetail extends EventTypeDetail> = Partial<
    Record<EventTypes, EventBusStoreCallback<TDetail>>
>;
interface ListenOptions<TDetail extends EventTypeDetail> {
    callback: EventBusStoreCallback<TDetail>;
    updateRequiredPeerTracking?: boolean;
    runOnce?: boolean;
}

export class EventBusStore extends BaseStore {
    callbacks: EventBusStoreCallbacks<any> | undefined;
    eventCache: Map<string, string> = new Map();
    internalEventManager: EventManager;

    @observable.ref requiredPeerCallbackTracking: Record<string, boolean> = {};

    @computed
    public get requiredPeerCallbacksCompleted() {
        if (Object.keys(this.requiredPeerCallbackTracking).length === 0) {
            return true;
        }
        let completed = true;
        for (const name in this.requiredPeerCallbackTracking) {
            if (!this.requiredPeerCallbackTracking[name]) {
                completed = false;
                break;
            }
        }
        return completed;
    }
    
    /**
     * Set this to true if we want the AppStoreBase to track child app initializations (e.g. grid app)
     */
    private isChildCallbackTrackingRequired = false;
    /**
     * If isChildCallbackTrackingRequired is true, the AppStoreBase will track this boolean to indicate to the app when it should bootstrap.
     */
    private areChildCallbacksCompleted = false;

    /**
     * Use to validate if child callbacks are completed but also considering if we should be tracking it.
     *
     * @readonly
     */
    public get requiredChildCallbacksCompleted() {
        if (!this.isChildCallbackTrackingRequired) {
            return true;
        }
        return this.areChildCallbacksCompleted;
    }

    /**
     * Call this if you require child callback tracking
     */
    public enableChildCallbackTracking() {
        this.isChildCallbackTrackingRequired = true;
    }

    /**
     * Update areChildCallbacksCompleted, which will be tracked via requiredChildCallbacksCompleted in the AppStoreBase
     *
     * @param state
     */
    public setAreChildCallbacksCompleted(state: boolean) {
        this.areChildCallbacksCompleted = state;
    }

    get eventManager(): EventManager {
        if (!this.internalEventManager) {
            this.internalEventManager = EventManager.get();
        }
        return this.internalEventManager;
    }

    @computed
    get configStore() {
        return this.stores.data.config;
    }

    @computed
    get dataStore() {
        return this.stores.data;
    }

    @computed
    get inputsStore() {
        return this.stores.inputsStore;
    }

    @computed
    get listenFor(): Array<ListenForOptions> {
        const listenFor = this.configStore && this.configStore.listenFor || [];
        return listenFor;
    }

    async initialize(callbacks: EventBusStoreCallbacks<any> = {}) {
        const defaultEvents = {};

        if (this.isListeningForEvent(EventTypes.GLOBAL_INPUTS_CHANGE)) {
            defaultEvents[EventTypes.GLOBAL_INPUTS_CHANGE] = this.inputsStore.handleUpdate;
        }

        if (this.isListeningForEvent(EventTypes.GLOBAL_SEARCH_CHANGE)) {
            defaultEvents[EventTypes.GLOBAL_SEARCH_CHANGE] = this.stores.data.handleSearchChange;
        }

        if (this.isListeningForEvent(EventTypes.PAGINATION_CHANGE) && this.stores.paginationStore) {
            defaultEvents[EventTypes.PAGINATION_CHANGE] = this.stores.paginationStore.handleChangeEvent;
        }
        
        this.callbacks = {
            ...defaultEvents,
            ...callbacks,
        };
    }

    isListeningFor(appId: string, eventName: string): boolean {
        const app = this.listenFor.find((appConfig) => {
            return appConfig.appId === appId;
        });
        const eventConfig =
            app &&
            app.eventTypes.find((evConfig) => {
                return evConfig.name === eventName;
            });

        return Boolean(eventConfig);
    }

    isListeningForEvent(eventType: EventTypes) {
        return this.listenFor.some((listener) => listener.eventTypes.some((et) => et.name === eventType));
    }

    /**
     * Start listening for events that are required for initial load
     */
    public register() {
        const listenForEvents = this.listenFor;
        if (listenForEvents.length > 0 && this.eventManager !== undefined) {
            listenForEvents.forEach((listenForEvent) => {
                const appId = listenForEvent.appId;
                const appEventTypes = listenForEvent.eventTypes;

                if (appEventTypes.length > 0) {
                    appEventTypes.forEach((appEvent) => {
                        const eventType = appEvent.name as EventTypes;
                        const requiredForInitialLoad = appEvent.requiredForInitialLoad;
                        const runOnce = appEvent.runOnce;
                        const cache = appEvent.cache;
                        // Wait for the eventBus Store to retrieve all events that are required before continuing with initialzing of the app
                        const targetCallback = this.callbacks && this.callbacks[eventType];
                        if (targetCallback) {
                            const storageValue = this.getStorageValue(appId, eventType, cache);

                            if (storageValue && this.hasEventDetailChanged(storageValue.detail, eventType)) {
                                this.log('debug', { message: `Found storage for app ${appId} and event ${eventType}` });
                                targetCallback({
                                    detail: storageValue,
                                    source: {
                                        templateId: `EVENT_FROM_STORAGE-${appId}_${eventType}`
                                    }
                                });
                            }

                            // Checks if the event is required and if we already have the event, then we don't need to set up additional tracking for it
                            let updateRequiredPeerTracking = false;
                            if (!storageValue && requiredForInitialLoad) {
                                updateRequiredPeerTracking = true;
                                this.requiredPeerCallbackTracking = {
                                    ...this.requiredPeerCallbackTracking,
                                    [`${appId}-${eventType}`]: false,
                                };
                                this.log('debug', { message: `Register require event ${eventType}` });
                            }

                            // add an event listener if the runOnce condition hasn't already been met.
                            if (!runOnce || (runOnce && !storageValue)) {
                                this.log('debug', { message: `Register event ${eventType}` });
                                this.listen(appId, eventType, {
                                    runOnce,
                                    updateRequiredPeerTracking,
                                    callback: targetCallback,
                                });
                            }
                        }
                    });
                }
            });
        }
    }

    /**
     * Trigger event types passing the appropriate event detail, this method also handles common functionality like appending source application details.
     *
     * @template T
     * @param type
     * @param eventTypeDetail
     */
    public trigger<T extends EventTypeDetail>(eventTypeDetail: T) {
        if (this.eventManager) {
            this.log('info', {
                message: `Triggering ${eventTypeDetail.type}`,
                detail: eventTypeDetail
            });
            const eventSource = {
                ...this.dataStore.eventSource,
                applicationId: this.configStore.applicationId,
            }
            this.eventManager.publish(this.dataStore.eventSourceKey, eventSource, eventTypeDetail);
        } else {
            this.log('warning', {
                message: `Unable to trigger event, as the event manager is not yet defined. The event manager is defined as part of the event bus store register.`,
                detail: eventTypeDetail,
            });
        }
    }

    /**
     * Generate an application identifier based on sourced application details, which are derived from props and configuration of the source app.
     *
     * @param application
     * @returns
     */
    public generateSourceAppKey(application: EventSource): string {
        // TODO: Remove currently used in tabs (tabs to be updated to work like grid)
        const { applicationCode, applicationId, configurationKey, templateId, styleKey } = application;
        const parts = [];
        if (templateId) {
            parts.push(`ti:${templateId}`);
        }
        if (configurationKey) {
            parts.push(`ck:${configurationKey}`);
        }
        if (styleKey) {
            parts.push(`sk:${styleKey}`);
        }
        if (applicationCode) {
            parts.push(`ac:${applicationCode}`);
        }
        if (applicationId) {
            parts.push(`ai:${applicationId}`);
        }
        return parts.join('-');
    }

    /**
     * Retrieves the application id from event detail's application source or the legacy identifier field
     *
     * @private
     * @template T
     * @param event
     * @returns
     */
    public getSourceAppIdFromEvent<T extends EventTypeDetail>(event: EventPublish<T>): string {
        // TODO: Review, used in input store, can we just use SourceEventManager.generateKey?
        const { source } = event;
        if (!source) {
            return '';
        }
        if (source.applicationId) {
            return source.applicationId;
        } else {
            return SourceEventManager.generateKey(source);
        }
    }

    /**
     * Determines Validates the to see if the last received event detail for a given event type is the same as the new event detail, regardless of source application.
     *
     * Note: This then keeps track of the state of the event type's last received event detail, and thus allows us to ignore duplicate updates.
     *
     * @param eventValue
     * @param appId
     * @param eventType
     * @returns
     */
    private hasEventDetailChanged(eventValue: Record<string, any>, eventType: string) {
        // These events should always change, regardless if they have a cached result.
        const cacheExempt: Array<EventTypes> = [
            EventTypes.PAGINATION_CHANGE,
            EventTypes.UPDATE_PAGINATION,
            EventTypes.CHANGE_ATTESTATION,
        ];
        if (cacheExempt.find((type) => type === eventType)) {
            return true;
        }
        const key = eventType;
        const evtValue = stringify(eventValue);
        this.log('debug', { message: `event cache key ${key}` });
        if (!this.eventCache.has(key) || this.eventCache.get(key) !== evtValue) {
            this.eventCache.set(key, evtValue);
            return true;
        }
        return false;
    }

    private getStorageValue(appId: string, eventType: EventTypes, cache?: EventTypeCache) {
        // These events should not rely on storage for for initial load.
        const cacheExempt: Array<EventTypes> = [
            EventTypes.CHANGE_ATTESTATION
        ];
        if (cacheExempt.find((type) => type === eventType)) {
            return;
        }
        if (cache && cache.enabled === false) {
            return;
        }
        const eventDetails = WindowManager.get('eventDetails');
        if (eventDetails && eventDetails[appId] && eventDetails[appId][eventType]) {
            return eventDetails[appId][eventType];
        }
        return;
    }

	private log = (eventType: TLogType, options: LogOptions) => {
		let logger;
		const workingOptions = { 
            ...options, 
            additionalContext: 'EventBusStore', 
            configKey: this.dataStore.configurationKey ,
        };
		switch (eventType) {
			case 'info':
				logger = info;
				break;
			case 'success':
				logger = success;
				break;
			case 'warning':
				logger = warning;
				break;
			case 'error':
				logger = error;
				break;
			case 'debug':
			default:
				logger = debug;
				break;
		}
		logger(workingOptions, Manifest);
    };

    /**
     * listen, subscribes to an event type and triggers a callback when the event is published, which is tightly coupled to the listenFor configuration.
     * @param targetAppId string
     * @param type EventTypes
     * @param options IListenOptions
     */
    private listen<T extends EventTypeDetail>(targetAppId: string, type: EventTypes, options: ListenOptions<T>) {
        const eventManager = this.eventManager;
        if (eventManager) {
            const { callback, updateRequiredPeerTracking = false, runOnce = false } = options;
            const eventHandler = (evt: EventPublish<T>) => {
                const eventSourceAppId = evt.source.applicationId;
                if (eventSourceAppId === targetAppId) {
                    this.log('success', {
                        message: `Triggered ${type}`,
                        detail: {
                            eventSourceAppId,
                            targetAppId,
                            ...evt.detail,
                        },
                    });
                    if (runOnce) {
                        const listener = evt.source;
                        eventManager.unsubscribe(targetAppId, type, listener);
                    }
                    if (updateRequiredPeerTracking) {
                        this.requiredPeerCallbackTracking = {
                            ...this.requiredPeerCallbackTracking,
                            [`${targetAppId}-${type}`]: true,
                        };
                    }
                    if (this.hasEventDetailChanged(evt.detail, type)) {
                        callback(evt);
                    }
                }
            };
            const subscription: EventSubscription<EventTypes, EventTypeDetail> = {
                type,
                listener: this.dataStore.eventSource,
                onPublish: eventHandler,
            };
            eventManager.subscribe(targetAppId, subscription);
        } else {
            this.log('warning', {
                message: `Unable to subscribe to event type "${type}", as the event manager is not yet defined. It is defined as part of the event bus store register method.`,
            });
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/EventBusStore.ts